import type { TenantModuleActivationSummary } from '../billing';

type RpcError = { message?: string } | null;

type ModuleActivationRpcClient = {
  rpc: (
    fn: string,
    args: Record<string, unknown>,
  ) => PromiseLike<{ data: unknown; error: RpcError }>;
};

function errorMessage(error: RpcError) {
  return error?.message || 'Unable to load tenant module activations';
}

function isTransientActivationLookupError(message: string) {
  return /statement timeout|canceling statement due to statement timeout|timeout exceeded|network.*timeout/i.test(
    message,
  );
}

export async function fetchTenantModuleActivationSummaries(
  sb: ModuleActivationRpcClient,
  tenantId: string,
): Promise<TenantModuleActivationSummary[]> {
  let response = await sb.rpc('list_tenant_module_activation_summaries', {
    p_tenant_id: tenantId,
  });

  if (response.error) {
    const firstMessage = errorMessage(response.error);
    if (isTransientActivationLookupError(firstMessage)) {
      response = await sb.rpc('list_tenant_module_activation_summaries', {
        p_tenant_id: tenantId,
      });
    }
  }

  if (response.error) {
    throw new Error(errorMessage(response.error));
  }

  const { data } = response;
  if (!Array.isArray(data)) return [];
  const rows: TenantModuleActivationSummary[] = [];
  for (const row of data) {
    if (!row || typeof row !== 'object') continue;
    const value = row as Record<string, unknown>;
    rows.push({
      module_key: typeof value.module_key === 'string' ? value.module_key : null,
      activation_state: typeof value.activation_state === 'string' ? value.activation_state : null,
    });
  }
  return rows;
}
